import '../../styles/storyDetail.css';
import useStoryChatter from '../../hooks/useStoryChatter';
import ChatterPagination from './ChatterPagination';
import { useSearchParams } from 'react-router-dom';
import { isTestOverdue, TEST_OVERDUE_LABEL } from '../../utils/overdue';
import { useDynamicsRecordLink } from '../../hooks/useDynamicsRecordLink';
import { useDynamicsPage } from '../../hooks/useDynamicsPage';
/**
 * Page Test Client
 *
 * Affiche la liste des User Stories en Test Client sous forme de grille
 * avec des boutons pour approuver (done) ou refuser chaque US
 */

import UserStoryAttachments from './UserStoryAttachments';
import AcceptanceCriteriaGrid from './AcceptanceCriteriaGrid';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { CheckCircle, XCircle, Loader2, AlertCircle, Paperclip, Plus, Trash2, MessageSquare, Send, User } from 'lucide-react';
import { useProjectData } from "../../context/ProjectDataContext";
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorMessage from '../common/ErrorMessage';
import backendApi from '../../services/backendApi';
import Pagination from '../common/Pagination';
import FilterSelectionCard from '../common/FilterSelectionCard';
import { buildStoryPointRangeOptions, matchesStoryPointRanges, sortMilestoneOptionsByDeadline } from '../../utils/dashboardFilters';
import { loadDashboardFilter, saveDashboardFilter } from '../../utils/savedDashboardFilters';
import useAnimatedModalClose from '../../hooks/useAnimatedModalClose';
import { readTicketFile, selectTicketFiles } from '../../utils/ticketAttachments';
import '../../styles/horizonHome.css';
import '../../styles/horizonAssistance.css';
import '../../styles/horizonTesting.css';
import '../../styles/ticketSituation.css';

const getTestingPeriod = (raw, mode) => {
  const date = raw ? new Date(`${raw.slice(0, 10)}T12:00:00`) : null;
  if (!date || Number.isNaN(date.getTime())) return { key: 'unknown', label: 'Date inconnue', sortKey: '9999' };
  if (mode === 'year') return { key: String(date.getFullYear()), label: String(date.getFullYear()), sortKey: String(date.getFullYear()) };
  if (mode === 'month') { const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`; return { key, label: date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }), sortKey: key }; }
  if (mode === 'day') { const key = date.toISOString().slice(0, 10); return { key, label: date.toLocaleDateString('fr-FR'), sortKey: key }; }
  const start = new Date(date); start.setDate(date.getDate() - (date.getDay() + 6) % 7);
  const end = new Date(start); end.setDate(start.getDate() + 6);
  const key = start.toISOString().slice(0, 10);
  return { key, label: `${start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} – ${end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`, sortKey: key };
};

function AcceptanceCriteriaPreview({ story, onOpen }) {
  const validated = story.acceptanceCriteriaValidated || 0;
  const total = story.acceptanceCriteriaTotal || 0;

  return <div className="testing-ca-preview" aria-label={`Critères d’acceptation de ${story.code || story.name}`}>
    {total === 0 && (story.description
      ? <button type="button" className="testing-ca-text-link" onClick={event => { event.stopPropagation(); onOpen(story); }}>
          Voir les critères
        </button>
      : <span className="testing-ca-empty">Aucun CA</span>)}
    {total > 0 && <button type="button" className="testing-ca-count"
      aria-label={`Afficher les critères d’acceptation de ${story.code || story.name} : ${validated} validés sur ${total}`}
      onClick={event => { event.stopPropagation(); onOpen(story); }}>
      <strong>{validated} / {total}</strong><span>CA validés</span>
      <span className="testing-ca-count__open" aria-hidden="true">›</span>
    </button>}
  </div>;
}

const ClientTestingPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const overdueOnly = searchParams.get('overdue') === '1';
  const [updating, setUpdating] = useState(new Set());
  const [error, setError] = useState(null);
  const [approvedStories, setApprovedStories] = useState(new Set());
  const [rejectedStories, setRejectedStories] = useState(new Set());
  const [selectedMilestones, setSelectedMilestones] = useState(new Set());
  const [testState, setTestState] = useState('pending');
  useEffect(() => { if (overdueOnly) setTestState('pending'); }, [overdueOnly]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSPRanges, setSelectedSPRanges] = useState(new Set());
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [groupBy, setGroupBy] = useState('none');
  const [filterSaveState, setFilterSaveState] = useState('idle');
  const [hasSavedFilter, setHasSavedFilter] = useState(false);
  const [rejectingUs, setRejectingUs] = useState(null); // US en cours de refus (pour la modale)
  const [rejectFeedback, setRejectFeedback] = useState('');
  const [rejectAttachments, setRejectAttachments] = useState([]);
  const [readingRejectAttachments, setReadingRejectAttachments] = useState(false);
  const [viewingUs, setViewingUs] = useState(null); // US en cours de visualisation (modale détails)
  const [criteriaUs, setCriteriaUs] = useState(null); // US affichée dans la modale dédiée aux CA
  const [viewingUsHasCriteria, setViewingUsHasCriteria] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const dynamicsActionState = useRef(null);
  const initialOverdueOnly = useRef(overdueOnly);
  const criteriaRefreshTimer = useRef(null);

  // États chatter
  const [isSendingUsMessage, setIsSendingUsMessage] = useState(false);
  const [newUsMessage, setNewUsMessage] = useState('');
  const itemsPerPage = 10; // Nombre d'US par page pour la grille

  // Lire les données depuis le contexte partagé (pas de nouvel appel API)
  const {
    isAdmin = false,
    userStories,
    milestones = [],
    loading,
    error: dataError,
    refresh,
  } = useProjectData();

  const scheduleCriteriaRefresh = useCallback(() => {
    clearTimeout(criteriaRefreshTimer.current);
    criteriaRefreshTimer.current = setTimeout(() => {
      Promise.resolve(refresh({ silent: true })).catch(refreshError => {
        console.warn('Impossible de synchroniser la liste après la mise à jour du CA:', refreshError.message);
      });
    }, 250);
  }, [refresh]);

  useEffect(() => () => clearTimeout(criteriaRefreshTimer.current), []);

  const applySavedFilter = useCallback(saved => {
    setSearchQuery(typeof saved.searchQuery === 'string' ? saved.searchQuery : '');
    setSelectedMilestones(new Set(Array.isArray(saved.milestones) ? saved.milestones : []));
    setSelectedSPRanges(new Set(Array.isArray(saved.storyPointRanges) ? saved.storyPointRanges : []));
    setDateFrom(typeof saved.dateFrom === 'string' ? saved.dateFrom : '');
    setDateTo(typeof saved.dateTo === 'string' ? saved.dateTo : '');
    setGroupBy(['none', 'day', 'week', 'month', 'year'].includes(saved.groupBy) ? saved.groupBy : 'none');
    setTestState(['pending', 'correction', 'validated'].includes(saved.testState) ? saved.testState : 'pending');
  }, []);

  useEffect(() => {
    const saved = loadDashboardFilter('client-testing');
    setHasSavedFilter(Boolean(saved));
    if (saved && !initialOverdueOnly.current) applySavedFilter(saved);
    if (initialOverdueOnly.current) setTestState('pending');
  }, [applySavedFilter]);

  const stateOf = useCallback(us => {
    if (approvedStories.has(us.id)) return 'validated';
    if (rejectedStories.has(us.id)) return 'correction';
    if (us.category === 'Done' || [132, 305].includes(us.stageId)) return 'validated';
    return us.stageId === 255 ? 'correction' : 'pending';
  }, [approvedStories, rejectedStories]);
  const clientTestingStories = useMemo(() => userStories.filter(us =>
    ['Tests à valider', 'Test Client', 'Done'].includes(us.category) || [255, 132, 305].includes(us.stageId)
  ), [userStories]);
  const pendingCount = clientTestingStories.filter(us => stateOf(us) === 'pending').length;
  const overdueCount = clientTestingStories.filter(us => stateOf(us) === 'pending' && isTestOverdue(us)).length;
  const correctionCount = clientTestingStories.filter(us => stateOf(us) === 'correction').length;
  const validatedCount = clientTestingStories.filter(us => stateOf(us) === 'validated').length;
  const storyPointRangeOptions = useMemo(() => buildStoryPointRangeOptions(clientTestingStories), [clientTestingStories]);

  // Liste des Jalons avec leur nombre d'US
  const jalonesWithCount = React.useMemo(() => {
    const milestoneCounts = {};

    clientTestingStories.forEach(us => {
      const milestoneName = us.milestoneName || 'Sans Jalon';
      milestoneCounts[milestoneName] = (milestoneCounts[milestoneName] || 0) + 1;
    });

    return Object.entries(milestoneCounts).map(([name, count]) => ({ name, count }));
  }, [clientTestingStories]);
  const sortedMilestones = useMemo(() => sortMilestoneOptionsByDeadline(jalonesWithCount, milestones), [jalonesWithCount, milestones]);

  // Filtrer les US selon le Jalon sélectionné et la recherche
  const filteredStories = React.useMemo(() => {
    let filtered = testState === 'all' ? clientTestingStories : clientTestingStories.filter(us => stateOf(us) === testState);

    if (overdueOnly) filtered = filtered.filter(us => isTestOverdue(us));

    // Filtre par Jalon
    if (selectedMilestones.size > 0) filtered = filtered.filter(us => selectedMilestones.has(us.milestoneName || 'Sans Jalon'));

    if (dateFrom || dateTo) filtered = filtered.filter(us => {
      const date = us.dateTestClient?.slice(0, 10);
      return date && (!dateFrom || date >= dateFrom) && (!dateTo || date <= dateTo);
    });

    // Filtre par recherche
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(us => {
        const name = us.name?.toLowerCase() || '';
        const description = us.description?.toLowerCase() || '';
        const code = us.code?.toLowerCase() || '';

        return name.includes(query) || description.includes(query) || code.includes(query);
      });
    }

    if (selectedSPRanges.size > 0) filtered = filtered.filter(us => matchesStoryPointRanges(us.storyPoints, selectedSPRanges));

    return [...filtered].sort((a, b) => (a.dateTestClient || '9999').localeCompare(b.dateTestClient || '9999') || a.id - b.id);
  }, [clientTestingStories, selectedMilestones, searchQuery, selectedSPRanges, testState, stateOf, overdueOnly, dateFrom, dateTo]);

  /**
   * Réinitialiser la page lors du changement de filtres
   */
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedMilestones, searchQuery, selectedSPRanges, testState, overdueOnly, dateFrom, dateTo, groupBy]);

  /**
   * Pagination : calculer les US à afficher pour la page courante
   */
  const paginatedStories = useMemo(() => {
    const page = Math.min(currentPage, Math.max(1, Math.ceil(filteredStories.length / itemsPerPage)));
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredStories.slice(startIndex, endIndex);
  }, [filteredStories, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredStories.length / itemsPerPage);
  const groupedTestingStories = useMemo(() => {
    if (groupBy === 'none') return null;
    const groups = new Map();
    filteredStories.forEach(story => {
      const period = getTestingPeriod(story.dateTestClient, groupBy);
      if (!groups.has(period.key)) groups.set(period.key, { ...period, stories: [] });
      groups.get(period.key).stories.push(story);
    });
    return [...groups.values()].sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  }, [filteredStories, groupBy]);

  const toggleMilestone = useCallback(name => {
    setSelectedMilestones(current => { const next = new Set(current); if (next.has(name)) next.delete(name); else next.add(name); return next; });
  }, []);
  const toggleSPRange = useCallback(range => {
    setSelectedSPRanges(current => { const next = new Set(current); if (next.has(range)) next.delete(range); else next.add(range); return next; });
  }, []);
  const resetFilters = useCallback(() => {
    setSearchQuery(''); setSelectedMilestones(new Set()); setTestState('pending'); setSelectedSPRanges(new Set()); setDateFrom(''); setDateTo('');
    const params = new URLSearchParams(searchParams); params.delete('overdue'); setSearchParams(params);
  }, [searchParams, setSearchParams]);
  const saveCurrentFilter = useCallback(() => {
    const saved = saveDashboardFilter('client-testing', {
      searchQuery,
      milestones: [...selectedMilestones],
      storyPointRanges: [...selectedSPRanges],
      dateFrom,
      dateTo,
      groupBy,
      testState,
    });
    if (saved) setHasSavedFilter(true);
    setFilterSaveState(saved ? 'saved' : 'error');
    window.setTimeout(() => setFilterSaveState('idle'), 1800);
  }, [dateFrom, dateTo, groupBy, searchQuery, selectedMilestones, selectedSPRanges, testState]);
  const reloadSavedFilter = useCallback(() => {
    const saved = loadDashboardFilter('client-testing');
    if (!saved) return;
    const params = new URLSearchParams(searchParams); params.delete('overdue'); setSearchParams(params);
    applySavedFilter(saved);
  }, [applySavedFilter, searchParams, setSearchParams]);
  const formatDeliveryDate = value => value
    ? new Date(`${value.slice(0, 10)}T12:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

  /**
   * Formater la taille du fichier
   */
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  /**
   * Marquer une US comme Done (stage_id: 132)
   */
  const handleApprove = async (usId) => {
    if (!(isAdmin || userStories.some(us => us.id === usId && us.canDecide)) || approvedStories.has(usId) || userStories.some(us => us.id === usId && (us.category === 'Done' || [132, 305].includes(us.stageId)))) return;
    console.log('Approbation de la US:', usId);

    try {
      setUpdating(prev => new Set(prev).add(usId));
      setError(null);

      // La notification aux abonnés est gérée automatiquement côté backend (stageId 132)
      const data = await backendApi.updateTaskStage(usId, 132, null, []);
      console.log('✅ US approuvée:', data);

      // Marquer la US comme approuvée (la garder visible en vert)
      setApprovedStories(prev => new Set(prev).add(usId));

      setRejectedStories(prev => { const next = new Set(prev); next.delete(usId); return next; });
      // Fermer la modale de détails si elle est ouverte
      if (viewingUs && viewingUs.id === usId) {
        await viewModalExit.close();
      }
      await refresh();

    } catch (err) {
      console.error('Erreur lors de l\'approbation:', err);
      setError(`Erreur lors de l'approbation de la US #${usId}: ${err.message}`);
    } finally {
      setUpdating(prev => {
        const newSet = new Set(prev);
        newSet.delete(usId);
        return newSet;
      });
    }
  };

  // Charger les messages du chatter
  useEffect(() => { setNewUsMessage(''); }, [viewingUs?.id]);
  const chatter = useStoryChatter(viewingUs?.id);
  const { messages: usMessages, loading: loadingUsMessages, reload: fetchUsMessages } = chatter;

  // Envoyer un message
  const handleSendUsMessage = async (e) => {
    e.preventDefault();
    if (!newUsMessage.trim() || !viewingUs) return;
    setIsSendingUsMessage(true);
    try {
      await backendApi.createUserStoryMessage(viewingUs.id, newUsMessage);
      setNewUsMessage('');
      await fetchUsMessages(viewingUs.id);
    } catch (err) {
      console.error('Erreur envoi message:', err);
      alert(err.message || 'Erreur lors de l\'envoi du message');
    } finally {
      setIsSendingUsMessage(false);
    }
  };

  /**
   * Ouvrir la modale de détails
   */
  const openViewModal = (us) => {
    setViewingUs(us);
    setViewingUsHasCriteria(null);
  };

  const openCriteriaModal = (us) => {
    setCriteriaUs(us);
  };

  /**
   * Fermer la modale de détails
   */
  const closeViewModal = () => {
    setViewingUs(null);
  };

  const closeCriteriaModal = () => {
    setCriteriaUs(null);
  };

  /**
   * Ouvrir la modale de refus (depuis la carte ou la modale de détails)
   */
  const openRejectModal = (us) => {
    if (!(isAdmin || us.canDecide)) return;
    // Fermer la modale de détails si elle est ouverte
    if (viewingUs) {
      closeViewModal();
    }
    if (criteriaUs) {
      closeCriteriaModal();
    }
    setRejectingUs(us);
    setRejectFeedback('');
    setRejectAttachments([]);
    setError(null);
  };

  /**
   * Fermer la modale de refus
   */
  const closeRejectModal = () => {
    setRejectingUs(null);
    setRejectFeedback('');
    setRejectAttachments([]);
  };

  const viewModalExit = useAnimatedModalClose(closeViewModal);
  const criteriaModalExit = useAnimatedModalClose(closeCriteriaModal);
  const rejectModalExit = useAnimatedModalClose(closeRejectModal, { disabled: readingRejectAttachments || (!!rejectingUs && updating.has(rejectingUs.id)) });
  const closeViewAnimated = viewModalExit.close;
  const closeCriteriaAnimated = criteriaModalExit.close;
  const closeRejectAnimated = rejectModalExit.close;

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key !== 'Escape') return;
      if (rejectingUs) closeRejectAnimated();
      else if (criteriaUs) closeCriteriaAnimated();
      else if (viewingUs) closeViewAnimated();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [closeCriteriaAnimated, closeRejectAnimated, closeViewAnimated, criteriaUs, rejectingUs, viewingUs]);

  /**
   * Gérer l'ajout de fichiers dans la modale de refus
   */
  const handleRejectFileChange = async (e) => {
    const selection = selectTicketFiles(Array.from(e.target.files), rejectAttachments);
    e.target.value = '';
    if (selection.error) alert(selection.error);
    if (!selection.accepted.length) return;
    setReadingRejectAttachments(true);
    try {
      const files = await Promise.all(selection.accepted.map(readTicketFile));
      setRejectAttachments(previous => [...previous, ...files]);
    } catch {
      setError('Impossible de lire une pièce jointe.');
    } finally {
      setReadingRejectAttachments(false);
    }
  };

  /**
   * Supprimer un fichier de la modale de refus
   */
  const handleRemoveRejectFile = (index) => {
    setRejectAttachments(prev => prev.filter((_, i) => i !== index));
  };

  /**
   * Envoyer la demande de correction depuis la modale
   */
  const confirmReject = async (throwOnError = false) => {
    if (readingRejectAttachments) return false;
    if (!rejectingUs || !(isAdmin || rejectingUs.canDecide)) return false;

    // Vérifier que le feedback est rempli
    if (!rejectFeedback.trim()) {
      setError('⚠️ Le feedback est obligatoire pour refuser une User Story');
      return false;
    }

    try {
      setUpdating(prev => new Set(prev).add(rejectingUs.id));
      setError(null);

      // Déplacer vers le stage "Refaisage" (255) avec le feedback
      const feedbackWithStatus = `❌ REFUSÉ - À refaire\n\n${rejectFeedback}`;
      const data = await backendApi.updateTaskStage(rejectingUs.id, 255, feedbackWithStatus, rejectAttachments);
      console.log('✅ US déplacée vers stage Refaisage (255) avec feedback:', data);

      // Marquer la US comme refusée localement
      setRejectedStories(prev => new Set(prev).add(rejectingUs.id));
      setApprovedStories(prev => {
        const newSet = new Set(prev);
        newSet.delete(rejectingUs.id);
        return newSet;
      });

      // Fermer la modale
      rejectModalExit.close();
      await refresh();
      return true;

    } catch (err) {
      console.error('Erreur lors du refus:', err);
      setError(`Erreur lors du refus de la US #${rejectingUs.id}: ${err.message}`);
      if (throwOnError) throw err;
      return false;
    } finally {
      setUpdating(prev => {
        const newSet = new Set(prev);
        newSet.delete(rejectingUs.id);
        return newSet;
      });
    }
  };

  dynamicsActionState.current = {clientTestingStories,isAdmin,rejectingUs,rejectFeedback,confirmReject};
  useEffect(() => {
    const handleDynamicsAction = async event => {
      const {action,resolve,reject} = event.detail || {};
      try {
        const state = dynamicsActionState.current;
        const story = state.clientTestingStories.find(us => us.id === action?.taskId);
        if (!story || !(state.isAdmin || story.canDecide)) throw new Error('Vous ne pouvez pas demander une correction sur cette User Story.');
        if (action.type === 'prepare_correction') {
          setViewingUs(null);setRejectingUs(story);setRejectFeedback(String(action.feedback || '').trim());setRejectAttachments([]);setError(null);
          resolve?.({prepared:true});return;
        }
        if (action.type === 'submit_correction') {
          if (state.rejectingUs?.id !== story.id || !state.rejectFeedback.trim()) throw new Error('La demande de correction n’est plus prête. Recommencez avec votre feedback.');
          const sent = await state.confirmReject(true);
          if (!sent) throw new Error('La demande de correction n’a pas pu être enregistrée.');
          resolve?.({submitted:true});return;
        }
        throw new Error('Action Dynamix non prise en charge.');
      } catch(error) {reject?.(error);}
    };
    window.addEventListener('dynamics:ui-action',handleDynamicsAction);
    return () => window.removeEventListener('dynamics:ui-action',handleDynamicsAction);
  },[]);


  // Afficher le spinner de chargement
  useDynamicsRecordLink(clientTestingStories, openViewModal);
  useDynamicsPage({path:'/dashboard/client-testing',title:(rejectingUs || viewingUs)?.name || 'Tests à valider',kind:'tasks',recordIds:[...new Set([...paginatedStories.map(t => t.id), ...((rejectingUs || viewingUs) ? [(rejectingUs || viewingUs).id] : [])])],listRecordIds:paginatedStories.map(t => t.id),focusedRecordId:(rejectingUs || viewingUs)?.id || null,filters:{search:searchQuery, milestone:[...selectedMilestones], state:testState, overdue:overdueOnly, storyPoints:[...selectedSPRanges], dateFrom, dateTo, groupBy},ready:!loading});

  if (loading) {
    return (
      <div className="min-h-full bg-gray-50">
        <LoadingSpinner message="Chargement des User Stories..." />
      </div>
    );
  }

  // Afficher le message d'erreur
  if (dataError) {
    return (
      <div className="min-h-full bg-gray-50">
        <ErrorMessage message={dataError} onRetry={refresh} />
      </div>
    );
  }

  const renderTestingRow = us => <article key={us.id} className={`testing-row${isTestOverdue(us) ? ' testing-row--overdue' : ''}`} role="button" tabIndex={0} aria-label="Ouvrir le test" onClick={() => openViewModal(us)} onKeyDown={event => { if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); openViewModal(us); } }}>
    <div className="testing-story"><span className="testing-code">{us.code || `US-${us.id}`} <i>•</i> {us.epicName || us.milestoneName || 'Sans jalon'}</span><h2>{us.name}</h2></div>
    <AcceptanceCriteriaPreview story={us} onOpen={openCriteriaModal} />
    <div className="testing-meta">{isTestOverdue(us) && <strong>Nécessitant votre attention</strong>}<small>Livrée le {formatDeliveryDate(us.dateTestClient)}</small><b>{us.storyPoints || 0} SP</b></div>
    <span aria-hidden="true" className="testing-open">›</span>
  </article>;

  return (
    <div className="horizon-home horizon-testing">
      <div className="horizon-home-inner">
        <header className="horizon-header"><div className="horizon-header-row">
          <div><p className="horizon-breadcrumb">Validation de votre part</p><h1>Tests à valider</h1>
            <p className="horizon-subtitle">{pendingCount} User Stor{pendingCount === 1 ? 'y attend votre retour' : 'ies attendent votre retour'}{overdueCount > 0 && <>, dont <strong>{overdueCount} nécessitant votre attention</strong></>}.</p></div>
        </div></header>
        <div className={`testing-content testing-filter-system${filtersOpen ? ' is-filter-open' : ''}`}>
          <div className="ticket-situation__views testing-state-views" aria-label="Vues des tests">
            {[{value:'pending',label:'À tester',count:pendingCount},{value:'correction',label:'En correction',count:correctionCount},{value:'validated',label:'Validés',count:validatedCount}].map(view => <button key={view.value} type="button" aria-pressed={testState === view.value && !overdueOnly} onClick={() => { setTestState(view.value); const params = new URLSearchParams(searchParams); params.delete('overdue'); setSearchParams(params); }}>{view.label}<span>{view.count}</span></button>)}
          </div>

          <aside aria-hidden={!filtersOpen} inert={filtersOpen ? undefined : ''} style={{ visibility: filtersOpen ? 'visible' : 'hidden' }} className={`ticket-situation__filter-rail testing-filter-rail${filtersOpen ? ' is-open' : ''}`} aria-label="Filtres des tests">
            <section className="ticket-situation__inspector">
              <header><h2>Filtres et regroupement</h2><button type="button" onClick={resetFilters}>Réinitialiser</button></header>
              <div className="ticket-filter-section"><label className="ticket-situation__search ticket-situation__search--inside"><span aria-hidden="true">⌕</span><span className="sr-only">Recherche</span><input aria-label="Rechercher une User Story" type="search" value={searchQuery} onChange={event => setSearchQuery(event.target.value)} placeholder="Rechercher" /></label></div>
              <div className="ticket-filter-section ticket-filter-group-card" aria-label="Options de regroupement">
                <h3>Regroupement</h3>
                <div className="ticket-group-card__choices" role="group" aria-label="Regrouper par">{[{value:'none',label:'Aucun'},{value:'day',label:'Jour'},{value:'week',label:'Semaine'},{value:'month',label:'Mois'},{value:'year',label:'Année'}].map(option => <button key={option.value} type="button" aria-pressed={groupBy === option.value} onClick={() => setGroupBy(option.value)}>{option.label}</button>)}</div>
              </div>
              <div className="ticket-filter-section">
                <h3>Date de livraison</h3>
                <div className="ticket-filter-dates"><label>De<input aria-label="De" type="date" value={dateFrom} onChange={event => setDateFrom(event.target.value)} /></label><label>À<input aria-label="À" type="date" value={dateTo} onChange={event => setDateTo(event.target.value)} /></label></div>
              </div>
              <FilterSelectionCard title="Charge estimée" options={storyPointRangeOptions.map(range => ({ value: range.value, label: range.label, count: range.count }))} selected={selectedSPRanges} onToggle={toggleSPRange} emptyLabel="Aucune estimation" compact />
              <FilterSelectionCard title="Jalons proches" options={sortedMilestones.map(({ name, count }) => ({ value: name, label: name, count }))} selected={selectedMilestones} onToggle={toggleMilestone} emptyLabel="Aucun jalon" visibleCount={3} />
              <div className="ticket-filter-section testing-sort-section"><h3>Affichage</h3><div className="ticket-filter-row"><span>Tri</span><strong>Plus anciens</strong></div></div>
              <div className="ticket-save-actions">
                {hasSavedFilter && <button type="button" className="ticket-load-filter" onClick={reloadSavedFilter}>Charger le filtre enregistré</button>}
                <button type="button" aria-live="polite" className={`ticket-save-view${filterSaveState === 'saved' ? ' is-saved' : ''}`} onClick={saveCurrentFilter}>{filterSaveState === 'saved' ? 'Filtre enregistré' : filterSaveState === 'error' ? 'Enregistrement impossible' : 'Enregistrer le filtre'}</button>
              </div>
            </section>
          </aside>
          <button type="button" className="ticket-situation__filter-toggle testing-filter-toggle" aria-expanded={filtersOpen} onClick={() => setFiltersOpen(open => !open)}>⚙ {filtersOpen ? 'Masquer les filtres' : 'Afficher les filtres'}</button>
        {/* Message d'erreur */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-red-800">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-600 transition-colors"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

          <section key={`${testState}|${searchQuery}|${overdueOnly}|${dateFrom}|${dateTo}|${groupBy}|${currentPage}|${[...selectedSPRanges].join(',')}|${[...selectedMilestones].join(',')}`} className="horizon-panel testing-list filter-results-motion" aria-label="User Stories à vérifier">
            {filteredStories.length === 0 ? <p className="horizon-empty">Aucune User Story ne correspond aux filtres sélectionnés.</p> : groupedTestingStories ? groupedTestingStories.map(group => <section key={group.key} className="testing-group"><header><strong>{group.label}</strong><span>{group.stories.length} test{group.stories.length > 1 ? 's' : ''}</span></header>{group.stories.map(renderTestingRow)}</section>) : paginatedStories.map(renderTestingRow)}
            {!groupedTestingStories && totalPages > 1 && <Pagination currentPage={Math.min(currentPage, totalPages)} totalPages={totalPages} totalItems={filteredStories.length} itemsPerPage={itemsPerPage} onPageChange={setCurrentPage} />}
          </section>
          <p className="testing-rule-note" role={overdueOnly ? 'status' : undefined}><span aria-hidden="true">ⓘ</span>{TEST_OVERDUE_LABEL.replace('Nécessitant votre attention : ', 'Un test nécessite votre attention après ')}</p>
        </div>

        {/* Modale de détails - Grand popup */}
        {viewingUs && (
          <div className={`us-detail-overlay testing-detail ${viewModalExit.modalClassName} fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-6`} role="dialog" aria-modal="true" aria-label="Détail du test">
            <div className="bg-white rounded-lg us-detail-window w-full overflow-hidden flex flex-col shadow-2xl">
              {/* En-tête synthétique */}
              <header className="us-detail-header">
                <div className="us-detail-header__meta">
                  <strong>{viewingUs.code || `US-${viewingUs.id}`}</strong>
                  {viewingUs.milestoneName && <><span aria-hidden="true">•</span><span>{viewingUs.milestoneName}</span></>}
                  <em>{stateOf(viewingUs) === 'validated' ? 'Validée' : stateOf(viewingUs) === 'correction' ? 'En correction' : 'À tester'}</em>
                </div>
                <h2>{viewingUs.name}</h2>
                <button onClick={viewModalExit.close} aria-label="Fermer le test" className="us-detail-header__close">
                  <XCircle className="w-5 h-5" />
                </button>
              </header>

              {/* Corps en 2 colonnes */}
              <div className="testing-detail-body">
                {/* Colonne gauche — Détails US */}
                <div className="px-6 py-6">
                  <AcceptanceCriteriaGrid initialData={chatter.criteriaData} initialError={chatter.criteriaData ? null : chatter.error}
                    task={viewingUs}
                    editable={isAdmin || viewingUs.canDecide}
                    onAvailability={setViewingUsHasCriteria}
                    fallback={viewingUs.description ? (
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
                        Ce qu’il faut vérifier
                      </h3>
                      <div
                        className="text-base text-gray-700 prose prose-base max-w-none bg-gray-50 rounded-lg p-6 border border-gray-200"
                        dangerouslySetInnerHTML={{ __html: viewingUs.description }}
                      />
                    </div>
                    ) : null}
                    onChanged={next => {
                      setViewingUs(current => current ? {
                        ...current,
                        stageId: next.task.stageId,
                        category: String(next.task.stageName || '').toLowerCase().includes('done') ? 'Done' : current.category,
                      } : current);
                      scheduleCriteriaRefresh();
                    }}
                  />
                </div>

                {/* Colonne droite — Chatter */}
                <div className="us-chatter-pane testing-discussion bg-gray-50 flex flex-col border-l border-gray-200">
                  {/* Header chatter */}
                  <div className="us-detail-tabs">
                    <h3 className="sr-only">Vos échanges avec l’équipe</h3>
                    <p className="sr-only">Discussions relatives à ce test</p>
                    <span className="sr-only">({usMessages.length} sur cette page)</span>
                    <div className="us-detail-tabs__row">
                      <button type="button" className="is-active"><MessageSquare className="w-4 h-4" /> Échanges</button>
                      <UserStoryAttachments taskId={viewingUs.id} compact />
                      <span>{usMessages.length} message{usMessages.length > 1 ? 's' : ''}</span>
                    </div>
                  </div>

                  {/* Liste des messages */}
                  <div className="us-detail-composer p-4 bg-white border-t border-gray-200 flex-shrink-0">
                    <form onSubmit={handleSendUsMessage} className="flex flex-col gap-2">
                      <textarea
                        value={newUsMessage}
                        onChange={(e) => setNewUsMessage(e.target.value)}
                        placeholder="Écrire un commentaire..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-brand-purple resize-none text-sm"
                        rows="3"
                        disabled={isSendingUsMessage}
                      />
                      <div className="flex justify-end">
                        <button
                          type="submit"
                          disabled={isSendingUsMessage || !newUsMessage.trim()}
                          className="flex items-center gap-2 px-4 py-2 bg-brand-purple text-white rounded-lg hover:bg-brand-purple-dark transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSendingUsMessage ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Envoi...
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4" />
                              Envoyer
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                  <ChatterPagination chatter={chatter} />
                  <div className="chatter-message-list p-6 pb-0">
                    {loadingUsMessages ? (
                      <div className="flex items-center justify-center py-16">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-purple"></div>
                      </div>
                    ) : usMessages.length === 0 ? (
                      <div className="text-center py-16 bg-white rounded border border-gray-200">
                        <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-base text-gray-500 mb-2">Aucun message</p>
                        <p className="text-sm text-gray-400">Soyez le premier à commenter</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {usMessages.map((message) => {
                          const isEmail = message.messageType === 'email';
                          return (
                            <div
                              key={message.id}
                              className={`rounded-lg p-4 border shadow-sm ${isEmail ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'}`}
                            >
                              <div className="flex items-start gap-3 mb-3">
                                <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${isEmail ? 'bg-blue-200' : 'bg-brand-purple-lighter'}`}>
                                  {isEmail ? (
                                    <span className="text-base">📧</span>
                                  ) : (
                                    <User className="w-4 h-4 text-brand-purple" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-bold text-gray-900 truncate">{message.authorName}</p>
                                  <p className="text-sm text-gray-500">
                                    {message.date && new Date(message.date).toLocaleString('fr-FR', {
                                      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                                    })}
                                  </p>
                                </div>
                              </div>

                              {message.body && (
                                <div
                                  className="text-sm text-gray-700 prose prose-sm max-w-none"
                                  dangerouslySetInnerHTML={{ __html: message.body }}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Formulaire d'envoi */}


                </div>
              </div>

              {/* Footer avec boutons */}
              <footer className="us-detail-footer">
                <button onClick={viewModalExit.close} className="us-detail-footer__close">Fermer</button>
                {(isAdmin || viewingUs.canDecide) && <div className="us-detail-footer__actions">
                  {updating.has(viewingUs.id) ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
                      <span className="text-sm text-gray-600">Enregistrement...</span>
                    </div>
                  ) : stateOf(viewingUs) === 'validated' ? (
                    // Si approuvée
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openRejectModal(viewingUs);
                      }}
                      className="flex items-center gap-2 px-4 py-2.5 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg font-medium transition-colors"
                    >
                      <XCircle className="w-4 h-4" />
                      Demander une correction
                    </button>
                  ) : stateOf(viewingUs) === 'correction' ? (
                    // Si refusée
                    (isAdmin || viewingUs.canDecide) && !(viewingUsHasCriteria ?? viewingUs.hasAcceptanceCriteria) && (<button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleApprove(viewingUs.id);
                      }}
                      className="flex items-center gap-2 px-4 py-2.5 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg font-medium transition-colors"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Valider
                    </button>)
                  ) : (
                    // En attente
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openRejectModal(viewingUs);
                        }}
                        className="flex items-center gap-2 px-4 py-2.5 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg font-medium transition-colors"
                      >
                        <XCircle className="w-4 h-4" />
                        Demander une correction
                      </button>
                      {(isAdmin || viewingUs.canDecide) && !(viewingUsHasCriteria ?? viewingUs.hasAcceptanceCriteria) && (<button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleApprove(viewingUs.id);
                        }}
                        className="flex items-center gap-2 px-4 py-2.5 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg font-medium transition-colors"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Valider
                      </button>)}
                    </>
                  )}
                </div>}
              </footer>
            </div>
          </div>
        )}

        {/* Modale dédiée aux critères d'acceptation */}
        {criteriaUs && (
          <div className={`us-detail-overlay testing-detail ${criteriaModalExit.modalClassName} fixed inset-0 bg-black/50 z-[65] flex items-center justify-center p-6`}
            role="dialog" aria-modal="true" aria-label={`Critères d’acceptation de ${criteriaUs.code || criteriaUs.name}`}>
            <div className="bg-white rounded-lg us-detail-window criteria-detail-window w-full overflow-hidden flex flex-col shadow-2xl">
              <header className="us-detail-header">
                <div className="us-detail-header__meta">
                  <strong>{criteriaUs.code || `US-${criteriaUs.id}`}</strong>
                  {criteriaUs.milestoneName && <><span aria-hidden="true">•</span><span>{criteriaUs.milestoneName}</span></>}
                  <em>Critères d’acceptation</em>
                </div>
                <h2>{criteriaUs.name}</h2>
                <button onClick={criteriaModalExit.close} aria-label="Fermer les critères d’acceptation" className="us-detail-header__close">
                  <XCircle className="w-5 h-5" />
                </button>
              </header>
              <div className="criteria-detail-body">
                <AcceptanceCriteriaGrid
                  task={criteriaUs}
                  editable={isAdmin || criteriaUs.canDecide}
                  fallback={criteriaUs.description ? (
                    <div className="criteria-detail-legacy">
                      <h3>Critères d’acceptation</h3>
                      <div dangerouslySetInnerHTML={{ __html: criteriaUs.description }} />
                    </div>
                  ) : <p className="criteria-detail-empty">Aucun critère d’acceptation n’est renseigné.</p>}
                  onChanged={next => {
                    setCriteriaUs(current => current ? {
                      ...current,
                      acceptanceCriteriaValidated: next.progress.validated,
                      acceptanceCriteriaTotal: next.progress.total,
                      stageId: next.task.stageId,
                      category: String(next.task.stageName || '').toLowerCase().includes('done') ? 'Done' : current.category,
                    } : current);
                    scheduleCriteriaRefresh();
                  }}
                />
              </div>
              <footer className="us-detail-footer">
                <button onClick={criteriaModalExit.close} className="us-detail-footer__close">Fermer</button>
                {(isAdmin || criteriaUs.canDecide) && <div className="us-detail-footer__actions">
                  <button type="button" onClick={() => openRejectModal(criteriaUs)}
                    className="flex items-center gap-2 px-4 py-2.5 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg font-medium transition-colors">
                    <XCircle className="w-4 h-4" />
                    Demander une correction
                  </button>
                </div>}
              </footer>
            </div>
          </div>
        )}

        {/* Modale de refus */}
        {rejectingUs && (
          <div className={`${rejectModalExit.modalClassName} fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4`} role="dialog" aria-modal="true" aria-label="Demander une correction">
            <div className="dashboard-modal__surface bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Demander une correction
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {rejectingUs.code || `US-${rejectingUs.id}`} - {rejectingUs.name}
                    </p>
                  </div>
                  <button
                    onClick={rejectModalExit.close}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    disabled={updating.has(rejectingUs.id) || readingRejectAttachments}
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Contenu */}
              <div className="px-6 py-5 space-y-5">
                {/* Feedback obligatoire */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Feedback <span className="text-red-600">*</span>
                  </label>
                  <textarea
                    value={rejectFeedback}
                    onChange={(e) => setRejectFeedback(e.target.value)}
                    placeholder="Expliquez pourquoi vous refusez cette User Story..."
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-none"
                    disabled={updating.has(rejectingUs.id)}
                  />
                  <p className="text-xs text-gray-500 mt-1.5">
                    Ce feedback sera envoyé à l'équipe de développement
                  </p>
                </div>

                {/* Pièces jointes */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Pièces jointes (optionnel)
                  </label>

                  {/* Liste des fichiers */}
                  {rejectAttachments.length > 0 && (
                    <div className="mb-3 space-y-2">
                      {rejectAttachments.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <div className="flex items-center gap-2.5 flex-1 min-w-0">
                            <Paperclip className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {file.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatFileSize(file.size)}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveRejectFile(index)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            disabled={updating.has(rejectingUs.id) || readingRejectAttachments}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Bouton d'ajout */}
                  <label className="flex items-center justify-center gap-2 w-full px-4 py-2.5 border border-dashed border-gray-300 rounded-lg hover:border-indigo-400 hover:bg-indigo-50/50 transition-colors cursor-pointer group">
                    <Plus className="w-4 h-4 text-gray-400 group-hover:text-indigo-600" />
                    <span className="text-sm text-gray-600 group-hover:text-indigo-700">
                      Ajouter des pièces jointes (Word, Excel, vidéos…)
                    </span>
                    <input
                      type="file"
                      multiple
                      onChange={handleRejectFileChange}
                      className="hidden"
                      disabled={updating.has(rejectingUs.id) || readingRejectAttachments}
                    />
                  </label>
                  <p className="text-xs text-gray-500 mt-1.5">
                    10 Mo par document, 30 Mo par vidéo, 40 Mo au total (10 fichiers maximum).
                  </p>
                </div>

                {/* Message d'erreur */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3 sticky bottom-0 bg-white">
                <button
                  onClick={rejectModalExit.close}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  disabled={updating.has(rejectingUs.id) || readingRejectAttachments}
                >
                  Annuler
                </button>
                <button
                  onClick={() => confirmReject()}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  disabled={updating.has(rejectingUs.id) || readingRejectAttachments || !rejectFeedback.trim()}
                >
                  {updating.has(rejectingUs.id) || readingRejectAttachments ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {readingRejectAttachments ? 'Préparation des fichiers…' : 'Refus en cours...'}
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4" />
                      Envoyer la demande de correction
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientTestingPage;
